/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { change, reduxForm, submit } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import reducer from './reducer';
import { getLocalStorageItem } from '../../utils/localStorage';
import { AccreditationCompanyForm } from './accreditation-company-form';
import { FORM_NAME } from './constants';
import {
	getIsSendingAccreditationSelector,
	getOrgTypesSelector,
	getIsOrgTypesLoadingSelector,
	getTaxSystemsSelector,
	getIsTaxSystemsLoadingSelector,
	getTaxRatesSelector,
	getIsTaxRatesLoadingSelector,
	getTaxSystemIdSelector,
	getFormInitialValuesSelector,
} from './selectors';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { getFromStorage } from '../../components/sidebar/utils';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import {
	getCompanyInfoAction,
	getOrgTypesAction,
	getTaxRatesAction,
	getTaxSystemsAction,
	sendAccreditationAction,
	setTaxRatesAction,
} from './actions';
import { useInjectReducer } from '../../utils/injectReducer';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { validate } from './utils';
import { getCompanyIdSelector } from '../profile/selectors';

const AccreditationCompanyModalWrapper = ({
	isSendingAccreditation,
	orgTypes,
	isOrgTypesLoading,
	taxSystems,
	isTaxSystemsLoading,
	taxRates,
	isTaxRatesLoading,
	getOrgTypes,
	getTaxSystems,
	getTaxRates,
	onSendAccreditation,
	onGetCompanyInfo,
	companyId,
	initialValues,
	onSetTaxRates,
	onFieldChange,
	onFormSubmit,
}) => {
	useInjectSaga({ key: 'accreditationCompanySaga', saga });
	useInjectReducer({ key: 'accreditationCompanyModalReducer', reducer });

	const history = useHistory();

	const user_data = getFromStorage('user_data');

	const [modalShow, setModalShow] = useState(false);

	useEffect(() => {
		if (user_data.email_verified_at && !user_data.filled_tax_at) {
			setModalShow(true);
		}
	}, [user_data]);

	useEffect(() => {
		if (companyId) onGetCompanyInfo(companyId);
	}, [companyId]);

	useEffect(() => {
		if (modalShow) {
			getOrgTypes();
			getTaxSystems();
		}
	}, [modalShow]);

	useEffect(() => {
		if (initialValues?.company?.tax_system_id) {
			getTaxRates({ taxSystemId: initialValues.company.tax_system_id });
		}
	}, [initialValues?.company?.tax_system_id]);

	useEffect(() => {
		const user_data = getLocalStorageItem('user_data');
		if (history.location.pathname.includes('/company-accreditation') && user_data.filled_tax_at) {
			history.push('/home/information');
		}
	}, []);

	const handleCompanyFormSubmit = async () => {
		await onFormSubmit(FORM_NAME);
		onSendAccreditation({
			id: user_data.companyId,
			redirect: history.push,
		});
	};

	const handleTaxSystemChange = value => {
		if (value) {
			onFieldChange(FORM_NAME, 'company.tax_rate_id', '');
			onSetTaxRates([]);
			getTaxRates({ taxSystemId: value });
		} else {
			onFieldChange(FORM_NAME, 'company.tax_rate_id', '');
			onSetTaxRates([]);
		}
	};

	return (
		<ModalWindow
			isModalShown={modalShow}
			modalContent={
				<AccreditationCompanyForm
					isSendingAccreditation={isSendingAccreditation}
					orgTypes={orgTypes}
					isOrgTypesLoading={isOrgTypesLoading}
					taxSystems={taxSystems}
					isTaxSystemsLoading={isTaxSystemsLoading}
					taxRates={taxRates}
					isTaxRatesLoading={isTaxRatesLoading}
					onTaxSystemChange={handleTaxSystemChange}
				/>
			}
			headerText="Аккредитация"
			closeOnDimmerClick={false}
			closeOnEscape={false}
			footerButtons={
				<ModalFooterButtons
					rightButtons={[
						{
							onClick: handleCompanyFormSubmit,
							text: 'Подтвердить',
							color: 'primary',
							loading: isSendingAccreditation,
							disabled:
								isSendingAccreditation || isOrgTypesLoading || isTaxSystemsLoading || isTaxRatesLoading,
						},
					]}
				/>
			}
		/>
	);
};

const AccreditationCompanyModalWithForm = reduxForm({
	form: FORM_NAME,
	enableReinitialize: true,
	onSubmit: validate,
})(AccreditationCompanyModalWrapper);

const mapStateToProps = createStructuredSelector({
	isSendingAccreditation: getIsSendingAccreditationSelector(),
	orgTypes: getOrgTypesSelector(),
	isOrgTypesLoading: getIsOrgTypesLoadingSelector(),
	taxSystems: getTaxSystemsSelector(),
	isTaxSystemsLoading: getIsTaxSystemsLoadingSelector(),
	taxRates: getTaxRatesSelector(),
	isTaxRatesLoading: getIsTaxRatesLoadingSelector(),
	taxSystemId: getTaxSystemIdSelector(),
	companyId: getCompanyIdSelector(),
	initialValues: getFormInitialValuesSelector(),
});

const mapDispatchToProps = {
	getOrgTypes: getOrgTypesAction,
	getTaxSystems: getTaxSystemsAction,
	getTaxRates: getTaxRatesAction,
	onSendAccreditation: sendAccreditationAction,
	onGetCompanyInfo: getCompanyInfoAction,
	onFieldChange: change,
	onSetTaxRates: setTaxRatesAction,
	onFormSubmit: submit,
};

AccreditationCompanyModalWrapper.propTypes = {
	isSendingAccreditation: PropTypes.bool.isRequired,
	orgTypes: PropTypes.array.isRequired,
	isOrgTypesLoading: PropTypes.bool.isRequired,
	taxSystems: PropTypes.array.isRequired,
	isTaxSystemsLoading: PropTypes.bool.isRequired,
	taxRates: PropTypes.array.isRequired,
	isTaxRatesLoading: PropTypes.bool.isRequired,
	taxSystemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	getOrgTypes: PropTypes.func.isRequired,
	getTaxSystems: PropTypes.func.isRequired,
	getTaxRates: PropTypes.func.isRequired,
	onSendAccreditation: PropTypes.func.isRequired,
	onGetCompanyInfo: PropTypes.func.isRequired,
	companyId: PropTypes.number,
	initialValues: PropTypes.object,
	onFieldChange: PropTypes.func.isRequired,
	onSetTaxRates: PropTypes.func.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
};

const AccreditationCompanyModal = connect(mapStateToProps, mapDispatchToProps)(AccreditationCompanyModalWithForm);

export default AccreditationCompanyModal;
